import type { DrawnBeam } from './drawn';
import { ENGRAVING } from './smufl';

export interface BeamObstacle {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** Translate a complete beam stack outward, preserving its slope and spacing.
 * Check the slope over each glyph's actual horizontal footprint. Moving past
 * one glyph can meet another, so reconsider all obstacles after every move. */
export function beamClearanceShift(
  beams: readonly DrawnBeam[],
  obstacles: readonly BeamObstacle[],
  up: boolean,
): number {
  let shift = 0;
  const gap = 0.25;
  // Each move permanently clears at least one beam/obstacle pair outward.
  // A later subdivision can meet the same glyph on the next move.
  for (let pass = 0; pass <= beams.length * obstacles.length; pass++) {
    let move = 0;
    for (const beam of beams) {
      const half = (ENGRAVING.beamThickness * (beam.grace ? ENGRAVING.graceScale : 1)) / 2;
      const at = (x: number) =>
        beam.y1 + shift + ((x - beam.x1) / (beam.x2 - beam.x1 || 1)) * (beam.y2 - beam.y1);
      for (const box of obstacles) {
        const lo = Math.max(box.left, Math.min(beam.x1, beam.x2));
        const hi = Math.min(box.right, Math.max(beam.x1, beam.x2));
        if (hi <= lo) continue;
        const top = Math.min(at(lo), at(hi)) - half;
        const bottom = Math.max(at(lo), at(hi)) + half;
        if (bottom <= box.top - gap + 1e-9 || top >= box.bottom + gap - 1e-9) continue;
        move = Math.max(move, up ? bottom - box.top + gap : box.bottom + gap - top);
      }
    }
    if (move <= 1e-9) break;
    shift += (up ? -1 : 1) * move;
  }
  return shift;
}
